import React, { FC } from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import { styled } from '@pixel/core/styles'
import {CircularProgress} from "@mui/material";
import LocalizedTypography from "../../components/LocalizedTypography";
import {LocalizedText} from "../../contexts/Localization/LocalizationTypes";
import {useMultipleLocalizedText} from "../../contexts/Localization/localizationHooks";
import TextField from "@pixel/core/TextField";
import MenuItem from "@pixel/core/MenuItem";
import useSecondaryAutomationFormValuesQuery, {
    DEFAULT_SECONDARY_AUTOMATIONS_DATA
} from "../hooks/useSecondaryAutomationFormValues";
import {AutomationSearchResult} from "../types/Automations";
import IconButton from "@pixel/core/IconButton";
import DeleteIcon from "@pixel/assets/icons/Delete";

const SecondaryAutomationContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    marginTop: theme.spacing(2),
}))

const DeleteContainer = styled('div')(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
}))

const SecondaryAutomationLoadingContainer = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
}))

interface SecondaryAutomationSectionProps {
    selectedAutomation: AutomationSearchResult
}

const SecondaryAutomationSection: FC<SecondaryAutomationSectionProps> = ({
    selectedAutomation
}) => {

    const { control, setValue } = useFormContext()

    const selectedSecondaryAutomation = useWatch({ name: 'linkedAutomations[0].secondaryCompanyAutomation.companyAutomationId', control, defaultValue: null })

    const {
        AUTOMATION_INPUT_NO_OPTIONS,
        AUTOMATION_TITLE,
    } = useMultipleLocalizedText()

    const companyAutomationId = selectedAutomation?.companyAutomationId;

    const {
        data,
        status,
    } = useSecondaryAutomationFormValuesQuery({
        companyAutomationId
    })

    const { SECONDARY_AUTOMATION } = data || DEFAULT_SECONDARY_AUTOMATIONS_DATA

    if (status === 'loading') {
        return (
            <SecondaryAutomationLoadingContainer>
                <CircularProgress size="32px" />
            </SecondaryAutomationLoadingContainer>
        )
    }

    if (status === 'error') {
        return (
            <SecondaryAutomationLoadingContainer>
                <LocalizedTypography color="error" text={LocalizedText.AUTOMATION_FORM_ERROR_CONTINUE} />
            </SecondaryAutomationLoadingContainer>
        )
    }

    return (
        <SecondaryAutomationContainer>
            <Controller
                control={control}
                name={`linkedAutomations[0].secondaryCompanyAutomation.companyAutomationId`}
                render={({ field, fieldState, }) => {
                    if (typeof field.value === 'undefined' && selectedAutomation?.linkedAutomations?.length > 0) {
                        return null
                    }
                    return (
                        <>
                            <TextField
                                id='automation-form-secondary-automation-field'
                                select
                                label={AUTOMATION_TITLE}
                                value={field.value || ''}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                error={fieldState.invalid}
                                errorText={fieldState?.error?.message || ''}
                                fullWidth
                            >
                                {
                                    SECONDARY_AUTOMATION.length === 0 ?
                                        (
                                            <MenuItem value="none" disabled>{AUTOMATION_INPUT_NO_OPTIONS}</MenuItem>
                                        ) :
                                        (
                                            SECONDARY_AUTOMATION.map((el: any) => {
                                                return (
                                                    <MenuItem key={el.companyAutomationId} value={el.companyAutomationId}>{el.name}</MenuItem>
                                                )})
                                        )
                                }
                            </TextField>
                            {
                                selectedSecondaryAutomation && (
                                    <DeleteContainer>
                                        <IconButton id='automation-form-secondary-automation-delete-button' onClick={() => {
                                                setValue('linkedAutomations[0].secondaryCompanyAutomation.companyAutomationId', null)
                                            }
                                        }><DeleteIcon/></IconButton>
                                    </DeleteContainer>
                                )
                            }
                        </>
                    );
                }}
            />
        </SecondaryAutomationContainer>
    );
}

export default SecondaryAutomationSection
